import {
  Component,
  OnInit,
  Input,
  ElementRef,
  Output,
  EventEmitter,
  OnDestroy,
  inject,
} from '@angular/core';

import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SearchFieldPipe } from '../pipes/search-field.pipe';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['dropdown.component.scss'],
  providers: [SearchFieldPipe],
  standalone: false,
})

export class DropdownComponent implements OnInit, OnDestroy {
  active!: boolean;
  defaultCssStyles: object = {
    toggle: '',
    dropdown: '',
  };
  selectedOptions: string[] = [];
  savedOptions: string[] = [];
  searchValue!: string;
  selectAll: boolean = false;

  @Input() disabled: boolean = false;
  @Input() cssStyles: any;
  @Input() dropDownName: string = '';
  @Input() menuDirection: string = 'right';
  @Input() hideOnMenuClick: boolean = true;
  @Input() isCheckbox: boolean = false;
  @Input() checkboxOptions?: string[] = [];
  @Input() checkboxName?: string;
  @Output() filters: EventEmitter<string[]> = new EventEmitter<string[]>();

  private clickSubscription!: Subscription;
  private readonly dropdown: ElementRef = inject(ElementRef);
  private readonly searchFieldPipe: SearchFieldPipe = inject(SearchFieldPipe);

  ngOnDestroy(): void {
    this.unsubscribeClick();
  }

  /**
   * If tooltip is disabled, then don't toggle it.
   * If tooltip is active (opened), then subscribe to click event for closing when you click somewhere except tooltip.
   * If tooltip is not active (closed), then unsubscribe from click event so the listener is not active.
   */
  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }
    this.active = !this.active;
    if (this.active) {
      this.subscribeClick();
    } else {
      this.unsubscribeClick();
    }
  }
  ngOnInit(): void {
    this.active = false;
    this.cssStyles = {...this.defaultCssStyles, ...this.cssStyles};
  }

  select(): void {
    if (this.hideOnMenuClick) {
      this.active = !this.active;
    }
  }

  selectOption(option: string): void {
    const index: number = this.selectedOptions.indexOf(option);
    if (index > -1) {
      this.selectedOptions.splice(index, 1);
    } else {
      this.selectedOptions.push(option);
    }
  }

  selectAllFilteredOptions(): void {
    if (!this.selectAll) {
      this.selectAll = true;
      const filteredOptions: string[] = this.searchFieldPipe.transform(this.checkboxOptions, this.searchValue);
      filteredOptions.forEach((option) => {
        this.selectedOptions.push(option);
      });
    } else {
      this.selectAll = false;
      this.selectedOptions = [];
    }
  }

  applyFilters(): void {
    this.savedOptions = [...this.selectedOptions];
    this.filters.emit(this.savedOptions);
    this.active = false;
  }

  cancel(): void {
    this.active = false;
    this.resetOptions();
  }

  resetOptions(): void {
    this.selectedOptions = [...this.savedOptions];
  }

  selectAndApply(option: string): void {
    this.selectOption(option);
    this.applyFilters();
  }

  /**
   * Subscribe to click event for closing dropdown when you click somewhere except dropdown.
   * @private
   */
  private subscribeClick(): void {
    this.clickSubscription = fromEvent(document, 'click')
      .pipe(
        filter((event: Event) => this.active && !this.dropdown.nativeElement.contains(event.target))
      ).subscribe(() => {
        this.active = false;
        this.unsubscribeClick();
      });
  }

  /**
   * Unsubscribe from click event, when dropdown is closed.
   * @private
   */
  private unsubscribeClick(): void {
    if (this.checkboxOptions?.length) {
      this.resetOptions();
    }
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
      this.clickSubscription = undefined!;
    }
  }
}
